import React, { useCallback, useState } from "react";
import { connect } from "react-redux";

import { faStar } from "@fortawesome/pro-solid-svg-icons/faStar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Col, Row } from "antd/es/grid";
import Switch from "antd/es/switch";
import { useIntl } from "react-intl";

import SavedFilterVisibility from "@mapmycustomers/shared/enum/SavedFilterVisibility";
import Team from "@mapmycustomers/shared/types/Team";
import { MapSavedFilter } from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Alert, Modal, TextField } from "@mapmycustomers/ui";

import VisibilitySelectField from "@app/component/input/VisibilitySelectField";
import { isCurrentUserManager, isCurrentUserMember, isCurrentUserOwner } from "@app/store/iam";
import { getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import useSavedFilterVisibilityOptions from "@app/util/hook/useSavedFilterVisibilityOptions";
import { getSavedFilterVisibilityNote } from "@app/util/ui";

import { MAX_FAVORITE_FILTERS } from "../util/consts";

import styles from "./AddEditFilterModal.module.scss";

export interface Props {
  filter?: MapSavedFilter;
  isFavorite?: boolean;
  isManager: boolean;
  isMember: boolean;
  isOwner: boolean;
  loading: boolean;
  okButtonText: string;
  onHide: () => void;
  onSave: (
    name: string,
    visibility: SavedFilterVisibility,
    favorite: boolean,
    teamId: Team["id"][],
    preventUpdateSavedFilter: boolean
  ) => void;
  reachedFavoriteFiltersLimit: boolean;
  teams: Team[];
  title: string;
}

const AddEditFilterModal: React.FC<Props> = ({
  filter,
  isFavorite = false,
  isManager,
  isMember,
  isOwner,
  loading,
  okButtonText,
  onHide,
  onSave,
  reachedFavoriteFiltersLimit,
  teams,
  title,
}) => {
  const intl = useIntl();
  const [name, setName] = useState<string>(filter?.name ?? "");
  const [error, setError] = useState<string | undefined>("");
  const [favorite, , , toggleFavorite] = useBoolean(isFavorite);
  const [visibility, setVisibility] = useState<SavedFilterVisibility>(
    filter?.visibility ?? SavedFilterVisibility.PRIVATE
  );
  const [teamId, setTeamId] = useState<Team["id"][]>(filter?.teamId ?? []);
  const visibilityOptions = useSavedFilterVisibilityOptions(isOwner, isManager);
  const handleCreateClick = useCallback(() => {
    if (name.trim().length) {
      setError(undefined);
      onSave(
        name,
        visibility,
        favorite,
        teamId,
        filter?.name === name && filter?.visibility === visibility && filter?.teamId === teamId
      );
    } else {
      setError(
        intl.formatMessage({
          id: "savedFilters.modal.input.error.empty",
          defaultMessage: "Name should not be empty",
          description: "Name field should not be empty error in Add/Edit Saved Filter modal",
        })
      );
    }
  }, [
    intl,
    favorite,
    filter?.name,
    filter?.teamId,
    filter?.visibility,
    name,
    onSave,
    setError,
    teamId,
    visibility,
  ]);

  const showAccessAlert = [
    SavedFilterVisibility.SHARED_WITH_ORGANIZATION,
    SavedFilterVisibility.SHARED_WITH_TEAM,
  ].includes(visibility);

  return (
    <Modal
      okButtonProps={{ loading }}
      okText={okButtonText}
      onCancel={onHide}
      onOk={handleCreateClick}
      title={title}
      visible
    >
      <p>
        {intl.formatMessage({
          id: "savedFilters.modal.description",
          defaultMessage:
            "Any currently applied filters, including filters by radius and group, will be saved. You can access Saved Filters any time from the top right of this page.",
          description: "Description of Saved Filter Add/Edit modal",
        })}
      </p>
      <TextField
        error={error}
        label={intl.formatMessage({
          id: "savedFilters.modal.input.label",
          defaultMessage: "Name your Saved Filter",
          description: "Name field in Add/Edit Saved Filter modal",
        })}
        onChange={setName}
        placeholder={intl.formatMessage({
          id: "savedFilters.modal.input.placeholder",
          defaultMessage: "e.g. Hot leads",
          description: "Name field placeholder in Add/Edit Saved Filter modal",
        })}
        size="large"
        value={name}
      />
      <VisibilitySelectField<SavedFilterVisibility>
        className={styles.visibility}
        disabled={isMember}
        label={intl.formatMessage({
          id: "savedFilterModal.field.visibility",
          defaultMessage: "Visible to",
          description: "Visibility field label in Create create edit saved filter modal",
        })}
        noteGetter={getSavedFilterVisibilityNote}
        onChange={setVisibility}
        onTeamIdsChange={setTeamId}
        options={visibilityOptions}
        teamIds={teamId}
        teams={teams}
        value={visibility}
      />
      {showAccessAlert && (
        <Alert
          className={styles.alert}
          message={intl.formatMessage({
            id: "savedFilters.modal.favoriteFilter.accessAlert",
            defaultMessage:
              "Users without view access to fields within this saved filter will not be able to apply it, even if it’s shared with them.",
            description: "Saved Filter favorite access alert message",
          })}
          showIcon
          type="info"
        />
      )}
      <Row align="middle" className={styles.favorite} justify="space-between">
        <Col>
          <FontAwesomeIcon className={styles.favoriteIcon} icon={faStar} size="xs" />
          {intl.formatMessage({
            id: "savedFilters.modal.favoriteFilter",
            defaultMessage: "Favorite this filter",
            description: "Saved Filter favorite text",
          })}
        </Col>
        <Col>
          <Switch
            checked={favorite}
            disabled={reachedFavoriteFiltersLimit && !isFavorite}
            onChange={() => toggleFavorite()}
          />
        </Col>
      </Row>
      <div className={styles.favoriteDescription}>
        {reachedFavoriteFiltersLimit && !isFavorite
          ? intl.formatMessage(
              {
                id: "savedFilters.modal.favoriteFilter.disallow",
                defaultMessage:
                  "Maximum of ({value}) {value, plural, one {favorited saved filter} other {favorited saved filters}} filters has been reached",
                description: "Saved Filter favorite disallow Description",
              },
              { value: MAX_FAVORITE_FILTERS }
            )
          : intl.formatMessage(
              {
                id: "savedFilters.modal.favoriteFilter.allow",
                defaultMessage:
                  "Favorite this filter to quickly access up to {value} {value, plural, one {saved filter} other {saved filters}} from various parts of the application. Favorites will only show as starred on your account, regardless of visibility.",
                description: "Saved Filter favorite allow Description",
              },
              { value: MAX_FAVORITE_FILTERS }
            )}
      </div>
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  isManager: isCurrentUserManager(state),
  isMember: isCurrentUserMember(state),
  isOwner: isCurrentUserOwner(state),
  teams: getTeams(state),
});

export default connect(mapStateToProps)(AddEditFilterModal);
